<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import ContactAPI from 'dashboard/api/contacts';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactMergeDuplicateModal from './ContactMergeDuplicateModal.vue';
import WahaDuplicateGroupCard from './WahaDuplicateGroupCard.vue';
import {
  clearReview,
  resolveTabForLid,
  setReviewStatus,
  SNOOZE_DAYS,
} from './wahaLidReviewStore';

// SiteUp (G6 / IF4-C): revisão em lote de duplicatas por waha_lid.
// Mockup aprovado: artifact ffb42a43 (tela 3). Sem "mesclar tudo".
// Persistência adiar/ignorar: localStorage por conta (sem backend novo).

const TAB_DEFS = [
  { id: 'pending', labelKey: 'CONTACTS_LAYOUT.WAHA_DUPLICATES.TABS.PENDING' },
  { id: 'snoozed', labelKey: 'CONTACTS_LAYOUT.WAHA_DUPLICATES.TABS.SNOOZED' },
  { id: 'ignored', labelKey: 'CONTACTS_LAYOUT.WAHA_DUPLICATES.TABS.IGNORED' },
];

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const isLoading = ref(true);
const loadError = ref(false);
const groups = ref([]);
const meta = ref({ count: 0, limited: false });
const activeTab = ref('pending');
// Bump força recompute dos filtros quando localStorage muda.
const reviewVersion = ref(0);

const showMergeModal = ref(false);
const mergeGroup = ref(null);

const tabCounts = computed(() => {
  // Dependência intencional de reviewVersion.
  // eslint-disable-next-line no-unused-expressions
  reviewVersion.value;
  const counts = { pending: 0, snoozed: 0, ignored: 0 };
  groups.value.forEach(group => {
    const tab = resolveTabForLid(accountId.value, group.waha_lid);
    counts[tab] += 1;
  });
  return counts;
});

const visibleGroups = computed(() => {
  // eslint-disable-next-line no-unused-expressions
  reviewVersion.value;
  return groups.value.filter(
    group =>
      resolveTabForLid(accountId.value, group.waha_lid) === activeTab.value
  );
});

const emptyMessage = computed(() => {
  if (activeTab.value === 'snoozed') {
    return t('CONTACTS_LAYOUT.WAHA_DUPLICATES.EMPTY.SNOOZED');
  }
  if (activeTab.value === 'ignored') {
    return t('CONTACTS_LAYOUT.WAHA_DUPLICATES.EMPTY.IGNORED');
  }
  return t('CONTACTS_LAYOUT.WAHA_DUPLICATES.EMPTY.PENDING');
});

const fetchGroups = async () => {
  isLoading.value = true;
  loadError.value = false;
  try {
    const { data } = await ContactAPI.getWahaDuplicates();
    groups.value = data?.payload || [];
    meta.value = data?.meta || { count: 0, limited: false };
  } catch (error) {
    loadError.value = true;
    groups.value = [];
    useAlert(t('CONTACTS_LAYOUT.WAHA_DUPLICATES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openMerge = group => {
  mergeGroup.value = group;
  showMergeModal.value = true;
};

const onMerged = payload => {
  const lid = payload?.wahaLid || mergeGroup.value?.waha_lid;
  if (lid) clearReview(accountId.value, lid);
  groups.value = groups.value.filter(group => group.waha_lid !== lid);
  mergeGroup.value = null;
  reviewVersion.value += 1;
};

const snoozeGroup = group => {
  setReviewStatus(accountId.value, group.waha_lid, 'snoozed');
  reviewVersion.value += 1;
  useAlert(
    t('CONTACTS_LAYOUT.WAHA_DUPLICATES.TOAST.SNOOZED', { days: SNOOZE_DAYS })
  );
};

const ignoreGroup = group => {
  setReviewStatus(accountId.value, group.waha_lid, 'ignored');
  reviewVersion.value += 1;
  useAlert(t('CONTACTS_LAYOUT.WAHA_DUPLICATES.TOAST.IGNORED'));
};

const restoreGroup = group => {
  clearReview(accountId.value, group.waha_lid);
  reviewVersion.value += 1;
  activeTab.value = 'pending';
  useAlert(t('CONTACTS_LAYOUT.WAHA_DUPLICATES.TOAST.RESTORED'));
};

const goToContacts = () => {
  router.push({
    name: 'contacts_dashboard_index',
    query: { page: 1 },
  });
};

onMounted(fetchGroups);
</script>

<template>
  <section
    class="flex h-full w-full flex-col overflow-hidden bg-n-background"
    data-testid="waha-duplicates-review"
  >
    <header
      class="flex flex-col gap-4 border-b border-n-weak px-6 py-6 mx-auto w-full max-w-5xl"
    >
      <nav class="flex items-center gap-2 text-sm text-n-slate-11">
        <button
          type="button"
          class="hover:text-n-slate-12 hover:underline"
          @click="goToContacts"
        >
          {{ t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS') }}
        </button>
        <span class="i-ri-arrow-right-s-line size-4 shrink-0" />
        <span class="font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.TITLE') }}
        </span>
      </nav>

      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="text-xl font-extrabold text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.TITLE') }}
          </h1>
          <p class="mt-1 max-w-2xl text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.SUBTITLE') }}
          </p>
        </div>
        <button
          type="button"
          class="min-h-11 inline-flex items-center gap-2 rounded-full border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="isLoading"
          data-testid="waha-dup-refresh"
          @click="fetchGroups"
        >
          <span
            class="i-ri-refresh-line size-4"
            :class="{ 'animate-spin': isLoading }"
          />
          {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.REFRESH') }}
        </button>
      </div>

      <div
        v-if="meta.limited"
        class="flex items-start gap-2 rounded-2xl border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
      >
        <span class="i-ri-alert-line size-3.5 shrink-0 mt-0.5" />
        {{
          t('CONTACTS_LAYOUT.WAHA_DUPLICATES.LIMITED_NOTICE', {
            count: meta.count,
          })
        }}
      </div>

      <div class="flex items-center gap-1 border-b border-n-weak">
        <button
          v-for="tab in TAB_DEFS"
          :key="tab.id"
          type="button"
          class="relative min-h-11 px-4 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === tab.id
              ? 'text-n-slate-12'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          :data-testid="`waha-dup-tab-${tab.id}`"
          @click="activeTab = tab.id"
        >
          {{ t(tab.labelKey) }}
          <span class="ml-1 tabular-nums text-n-slate-11">
            {{ tabCounts[tab.id] }}
          </span>
          <span
            v-if="activeTab === tab.id"
            class="absolute inset-x-3 -bottom-px h-1 rounded-full bg-n-blue-9"
          />
        </button>
      </div>
    </header>

    <div class="flex-1 overflow-y-auto px-6 py-6">
      <div class="mx-auto flex w-full max-w-5xl flex-col gap-4">
        <div v-if="isLoading" class="flex min-h-48 items-center justify-center">
          <Spinner />
        </div>

        <div
          v-else-if="loadError"
          class="flex min-h-48 flex-col items-center justify-center gap-3 text-center"
        >
          <p class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.LOAD_ERROR') }}
          </p>
          <button
            type="button"
            class="min-h-11 rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold text-n-background"
            @click="fetchGroups"
          >
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.RETRY') }}
          </button>
        </div>

        <div
          v-else-if="!visibleGroups.length"
          class="flex min-h-48 flex-col items-center justify-center gap-2 rounded-2xl border border-n-weak bg-n-solid-1 px-6 py-10 text-center"
          data-testid="waha-dup-empty"
        >
          <span class="i-ri-checkbox-circle-line size-8 text-n-slate-10" />
          <p class="text-sm font-medium text-n-slate-12">{{ emptyMessage }}</p>
          <p class="max-w-md text-xs text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.EMPTY.HINT') }}
          </p>
        </div>

        <template v-else>
          <WahaDuplicateGroupCard
            v-for="group in visibleGroups"
            :key="group.waha_lid"
            :group="group"
            :tab="activeTab"
            @merge="openMerge"
            @snooze="snoozeGroup"
            @ignore="ignoreGroup"
            @restore="restoreGroup"
          />
        </template>
      </div>
    </div>

    <ContactMergeDuplicateModal
      v-if="showMergeModal && mergeGroup"
      v-model:show="showMergeModal"
      :group="mergeGroup"
      @merged="onMerged"
    />
  </section>
</template>
